import { IContentDoc } from '@r3f-component-data-structure';
import { applyContentToStores, applyImageTrackingData, transitionManager } from '../content-apply';
import { useAssetPreloader as preloadAssets } from './useAssetPreloader';
import { Jute } from '..';
import { debugLog } from '../debug-log';

// Re-exported from its original home so existing importers (ArSceneContent, EntityLevelFadeGroup) don't
// move. The registry itself now lives in content-apply.ts, alongside the code that populates it.
export { transitionManager };

let contentLoaderCalled = false;

/**
 * The one-time boot load: push the fetched content doc into the stores, then fetch tracking data and
 * preload assets.
 *
 * Runs at most once per page — the guard is deliberate, since this is called during render. Live updates
 * do NOT come back through here; they call `Jute.applyContentUpdate()`, which shares the store-population
 * step via `applyContentToStores` but skips the scene-state resets that only make sense at boot.
 */
export const useContentLoader = (contentData: IContentDoc): void => {
	if (contentLoaderCalled) return;
	contentLoaderCalled = true;

	async function loadContent() {
		debugLog('loadContent: start');
		Jute.loading?.();
		const { componentsById, rootId, tracking } = applyContentToStores(contentData, { initial: true });
		debugLog('loadContent: stores populated — awaiting tracking data and asset preload');
		// Settled, not all: a failure in EITHER of these must not stop `Jute.loaded()` from firing.
		// `loading()` shows the launch screen's spinner and only `loaded()` takes it down, so a rejection
		// here used to leave the user watching that spinner forever with no visible cause — one
		// unresolvable asset taking down the whole experience.
		const results = await Promise.allSettled([
			applyImageTrackingData(tracking),
			preloadAssets({ componentsById, rootId })
		]);
		debugLog('loadContent: both steps settled', results.map(r => r.status).join(', '));
		for (const result of results) {
			if (result.status === 'rejected') console.error('jute content loader: a load step failed', result.reason);
		}
	}

	loadContent()
		.then(() => {
			console.log('assets loaded')
		})
		.catch((err) => {
			// applyContentToStores itself threw — the scene will be wrong, but showing a permanent spinner
			// tells the user nothing. Surface it and let JUTE launch so the failure is visible.
			console.error('jute content loader: content could not be applied', err);
		})
		.finally(() => {
			Jute.loaded?.();
		});
};
